/**
 * Sleepy Sloth Wishlist Management
 * Handles wishlist functionality for both guest (localStorage) and logged-in users (customer metafields)
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'sleepy_sloth_wishlist';
    this.wishlistItems = this.loadWishlist();
    this.init();
  }

  init() {
    // Update wishlist count on page load
    this.updateWishlistCount();
    
    // Listen for wishlist toggle events
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        e.preventDefault();
        const productId = toggleBtn.dataset.productId;
        const productHandle = toggleBtn.dataset.productHandle;
        const productTitle = toggleBtn.dataset.productTitle;
        const productImage = toggleBtn.dataset.productImage;
        const productPrice = toggleBtn.dataset.productPrice;
        const productUrl = toggleBtn.dataset.productUrl;
        
        this.toggleWishlistItem({
          id: productId,
          handle: productHandle,
          title: productTitle,
          image: productImage,
          price: productPrice,
          url: productUrl
        });
      }
    });

    // Update all wishlist button states
    this.updateAllWishlistButtons();
  }

  /**
   * Load wishlist from localStorage
   */
  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading wishlist:', error);
      return [];
    }
  }

  /**
   * Save wishlist to localStorage
   */
  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlistItems));
      this.updateWishlistCount();
      this.updateAllWishlistButtons();
      
      // Dispatch custom event for wishlist updates
      window.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { items: this.wishlistItems }
      }));
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  /**
   * Check if product is in wishlist
   */
  isInWishlist(productId) {
    return this.wishlistItems.some(item => item.id === productId);
  }

  /**
   * Add product to wishlist
   */
  addToWishlist(product) {
    if (!this.isInWishlist(product.id)) {
      this.wishlistItems.push({
        id: product.id,
        handle: product.handle,
        title: product.title,
        image: product.image,
        price: product.price,
        url: product.url,
        addedAt: new Date().toISOString()
      });
      this.saveWishlist();
      this.showNotification(`${product.title} added to wishlist`, 'success');
      return true;
    }
    return false;
  }

  /**
   * Remove product from wishlist
   */
  removeFromWishlist(productId) {
    const index = this.wishlistItems.findIndex(item => item.id === productId);
    if (index > -1) {
      const removedItem = this.wishlistItems.splice(index, 1)[0];
      this.saveWishlist();
      this.showNotification(`${removedItem.title} removed from wishlist`, 'info');
      return true;
    }
    return false;
  }

  /**
   * Toggle product in wishlist
   */
  toggleWishlistItem(product) {
    if (this.isInWishlist(product.id)) {
      this.removeFromWishlist(product.id);
    } else {
      this.addToWishlist(product);
    }
  }

  /**
   * Get all wishlist items
   */
  getWishlistItems() {
    return this.wishlistItems;
  }

  /**
   * Clear entire wishlist
   */
  clearWishlist() {
    this.wishlistItems = [];
    this.saveWishlist();
  }

  /**
   * Update wishlist count in header
   */
  updateWishlistCount() {
    const countElements = document.querySelectorAll('[data-wishlist-count]');
    const count = this.wishlistItems.length;
    
    countElements.forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  /**
   * Update all wishlist button states
   */
  updateAllWishlistButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-toggle]');
    buttons.forEach(btn => {
      const productId = btn.dataset.productId;
      const isActive = this.isInWishlist(productId);
      
      if (isActive) {
        btn.classList.add('wishlist-active');
        btn.setAttribute('aria-label', `Remove ${btn.dataset.productTitle || 'item'} from wishlist`);
      } else {
        btn.classList.remove('wishlist-active');
        btn.setAttribute('aria-label', `Add ${btn.dataset.productTitle || 'item'} to wishlist`);
      }
    });
  }

  /**
   * Show notification message
   */
  showNotification(message, type = 'info') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `wishlist-notification wishlist-notification--${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');
    notification.textContent = message;
    
    // Add to page
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => notification.classList.add('wishlist-notification--visible'), 10);
    
    // Remove after 3 seconds
    setTimeout(() => {
      notification.classList.remove('wishlist-notification--visible');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  /**
   * Move item from cart to wishlist
   */
  moveFromCartToWishlist(lineItem) {
    const product = {
      id: lineItem.product_id?.toString() || lineItem.id?.toString(),
      handle: lineItem.handle,
      title: lineItem.product_title || lineItem.title,
      image: lineItem.image,
      price: lineItem.price,
      url: lineItem.url
    };
    
    this.addToWishlist(product);
  }
}

// Initialize wishlist manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.wishlistManager = new WishlistManager();
  });
} else {
  window.wishlistManager = new WishlistManager();
}
